import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { isDrawnOpening, resolveMatchType } from '@draughts/shared';
import { useOnlineStore } from '../../store/online-store';
import { useAuthStore } from '../../store/auth-store';
import {
  acceptChallenge as socketAcceptChallenge,
  declineChallenge as socketDeclineChallenge,
} from '../../services/socket';
import { trackChallengeAccepted, trackChallengeDeclined } from '../../utils/analytics';
import { variantShortLabel } from '../../utils/variant-labels';
import { colorLeading, gameTypeLeading, gameModeLeading, openingLeading } from '../../utils/challenge-icons';
import { matchTypeValue, knownMatchTypeLabel, gameModeLabel } from '../../utils/challenge-labels';
import { openingOptionLabel } from '../../utils/opening-labels';
import { playSound } from '../../utils/audio';
import { vibrate } from '../../utils/vibration';
import { log } from '../../utils/logger';
import './GlobalIncomingChallengeBanner.css';

/**
 * Global incoming direct-challenge banner.
 *
 * Single, prominent presentation rendered on EVERY screen (mounted via
 * App's `challengeOverlays` slot) — it replaces both the dashboard's inline
 * strip and the legacy fullscreen ChallengeModal so an invite is impossible
 * to miss regardless of the current page. Adds a sound + browser-tab title
 * badge as extra alert cues. Auto-gates on `incomingChallenge`.
 */
export default function GlobalIncomingChallengeBanner() {
  const { t } = useTranslation();
  const challenge = useOnlineStore((s) => s.incomingChallenge);
  const setIncomingChallenge = useOnlineStore((s) => s.setIncomingChallenge);
  // Ospite o iscritto: è l'unica cosa che decide se questa partita conta (vedi
  // `tipoPartita` più sotto), e il negozio è l'unico posto che lo sa per certo.
  const ownIsGuest = useAuthStore((s) => s.isGuest);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  const challengeId = challenge?.id;
  const expiresAt = challenge?.expiresAt;

  // Countdown + auto-decline on expiry. Self-contained so the latest
  // challenge id is always captured; runs the first tick immediately to
  // avoid a spurious "0" before the interval starts.
  useEffect(() => {
    if (!challengeId || expiresAt == null) return;
    log.info('IncomingChallengeBanner', 'Challenge armed', {
      challengeId,
      secondsToExpiry: Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)),
    });
    let timer = 0;
    const tick = () => {
      const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining === 0) {
        window.clearInterval(timer);
        log.info('IncomingChallengeBanner', 'Challenge expired, auto-declining', { challengeId });
        trackChallengeDeclined();
        socketDeclineChallenge(challengeId).catch((e) =>
          log.error('IncomingChallengeBanner', 'Auto-decline emit failed', e)
        );
        setIncomingChallenge(null);
      }
    };
    timer = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(timer);
  }, [challengeId, expiresAt, setIncomingChallenge]);

  // Audio + haptic alert — fires once per incoming challenge (keyed on id).
  useEffect(() => {
    if (!challengeId) return;
    playSound('match-found');
    vibrate([120, 60, 120]);
  }, [challengeId]);

  // Browser-tab title badge — visible when the tab is in the background.
  // Captures the page title on arrival and restores it on clear/unmount.
  useEffect(() => {
    if (!challengeId) return;
    const original = document.title;
    document.title = `(1) ${t('online.challenge.tabBadge')} · ${original}`;
    return () => { document.title = original; };
  }, [challengeId, t]);

  if (!challenge) return null;

  const variantLabel = (variant: string): string => variantShortLabel(t, variant);

  const timeControlLabel = (tc: string): string => {
    if (tc === 'none') return t('game.timeControls.noTimeLimit');
    const parts = tc.split('-');
    if (parts.length === 2 && parts[0] && parts[1]) {
      const category = parts[0];
      return `${parts[1]} (${category.charAt(0).toUpperCase() + category.slice(1)})`;
    }
    return tc;
  };

  // The recipient plays the opposite of the challenger's chosen side; 'random'
  // (or a legacy challenge without a color) stays undecided until game start.
  const recipientColorLabel = (): string => {
    if (challenge.color === 'white') return t('game.clock.black');
    if (challenge.color === 'black') return t('game.clock.white');
    return t('online.challenge.colorRandom');
  };

  // The side value the recipient will play (opposite of the challenger's pick),
  // used to pick the matching colour glyph. 'random' → shuffle icon.
  const recipientColor =
    challenge.color === 'white' ? 'black' : challenge.color === 'black' ? 'white' : 'random';

  /*
   * «Valutata/Amichevole» qui non vuol dire niente: i giocatori sono questi due.
   * Chi sfida è per forza iscritto — il server rifiuta una sfida mandata da un
   * ospite (GUEST_CANNOT_CHALLENGE) — quindi la risposta la decide una cosa sola:
   * se chi legge questo striscione ha un punteggio da muovere.
   *
   * Lo zoccolo serve anche a server nuovo: una sfida nata prima che il server
   * risolvesse `'any'` da sé, o un client su un pacchetto in cache, arrivano
   * ancora con «qualsiasi».
   */
  const tipoPartita = resolveMatchType(
    matchTypeValue(challenge.matchType, challenge.rated),
    { isGuest: ownIsGuest },
  );

  // `null` per la partita normale — la pastiglia non compare — e l'etichetta della
  // modalità per ogni altra. Passa dalla funzione condivisa e non da una catena scritta
  // qui: chi riceve la sfida deve sapere COSA sta accettando, e la catena precedente
  // taceva ogni modalità aggiunta dopo di lei.
  const modeLabel = (): string | null =>
    challenge.gameMode && challenge.gameMode !== 'normal' ? gameModeLabel(t, challenge.gameMode) : null;

  const handleAccept = async () => {
    log.info('IncomingChallengeBanner', 'Accept clicked', { from: challenge.fromUsername });
    setLoading(true);
    trackChallengeAccepted();
    try {
      await socketAcceptChallenge(challenge.id);
      setIncomingChallenge(null);
    } catch (e) {
      log.error('IncomingChallengeBanner', 'Accept failed', e);
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    log.info('IncomingChallengeBanner', 'Decline clicked', { from: challenge.fromUsername });
    trackChallengeDeclined();
    try {
      await socketDeclineChallenge(challenge.id);
    } catch (e) { log.error('IncomingChallengeBanner', 'Decline emit failed', e); }
    setIncomingChallenge(null);
  };

  return (
    <div
      className="incoming-challenge-banner"
      role="alert"
      aria-live="assertive"
      aria-label={t('online.challenge.incomingChallenge')}
    >
      <div className="icb-card">
        <span className="icb-icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.5 17.5 3 6V3h3l11.5 11.5" />
            <path d="M13 19l6-6" />
            <path d="M16 16l4 4" />
            <path d="M19 21l2-2" />
          </svg>
        </span>

        <div className="icb-text">
          {/* Come per la rivincita: nessun numero per chi non ha punteggio. */}
          <Trans
            i18nKey={challenge.fromRating == null
              ? 'dashboard.notif.challengedYouNoRating'
              : 'dashboard.notif.challengedYou'}
            values={{
              name: challenge.fromUsername,
              rating: challenge.fromRating,
              timeControl: timeControlLabel(String(challenge.timeControl)),
              variant: variantLabel(String(challenge.variant)),
            }}
            components={{ b: <b /> }}
          />
          <div className="icb-meta">
            {modeLabel() && (
              <span className="icb-chip icb-chip-mode">
                {gameModeLeading(challenge.gameMode ?? 'normal', 12)}
                {modeLabel()}
              </span>
            )}
            {challenge.opening && isDrawnOpening(challenge.opening) && (
              <span className="icb-chip">
                {openingLeading(challenge.opening, 12)}
                {openingOptionLabel(t, challenge.opening, String(challenge.variant))}
              </span>
            )}
            {/* Andata e ritorno: due partite a colori invertiti — con la pescata,
                la stessa due volte. Fuori dal ramo dell'apertura: si può chiedere
                anche a mossa libera. Solo quando è stato chiesto davvero. */}
            {challenge.legs === 2 && <span className="icb-chip">{t('online.match.twoLegsShort')}</span>}
            <span className="icb-chip">
              {gameTypeLeading(tipoPartita, 12)}
              {knownMatchTypeLabel(t, tipoPartita)}
            </span>
            <span className="icb-chip">
              {colorLeading(recipientColor, 12)}
              {recipientColorLabel()}
            </span>
          </div>
        </div>

        <span className="icb-timer" aria-hidden="true">{timeLeft}s</span>

        <div className="icb-actions">
          <button type="button" className="icb-btn icb-decline" onClick={handleDecline} disabled={loading}>
            {t('online.challenge.decline')}
          </button>
          <button type="button" className="icb-btn icb-accept" onClick={handleAccept} disabled={loading}>
            {loading ? <span className="icb-spinner" /> : t('online.challenge.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
